import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useUpdateSavedSet } from "../../api/map-saved-sets/hooks";
import { showError } from "../../shared/toast";
import type { MapSavedSetDetails } from "../../api/map-saved-sets/types";
import type { MunicipalityOption } from "./MapLayersPanel";
import "./SavedSetCreateDialog.css";

interface SavedSetUpdateDialogProps {
  open: boolean;
  onClose: () => void;
  savedSet: MapSavedSetDetails | null;
  municipalityOptions: MunicipalityOption[];
  onSuccess: () => void;
}

export const SavedSetUpdateDialog = ({
  open,
  onClose,
  savedSet,
  municipalityOptions,
  onSuccess
}: SavedSetUpdateDialogProps): JSX.Element | null => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [municipalityId, setMunicipalityId] = useState("");
  const updateMutation = useUpdateSavedSet();

  useEffect(() => {
    if (open && savedSet) {
      setName(savedSet.name);
      setMunicipalityId(savedSet.municipalityPolygonId);
    }
  }, [open, savedSet]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!savedSet) return;
    const trimmed = name.trim();
    if (!trimmed) {
      showError(
        t("map.layers.updateSavedSetError", "Validation"),
        t("map.layers.nameRequired", "Name is required.")
      );
      return;
    }
    if (!municipalityId) {
      showError(
        t("map.layers.updateSavedSetError", "Validation"),
        t("map.layers.municipalityRequired", "Municipality is required.")
      );
      return;
    }
    try {
      await updateMutation.mutateAsync({
        id: savedSet.id,
        name: trimmed,
        municipalityPolygonId: municipalityId
      });
      onSuccess();
      onClose();
    } catch (err) {
      showError(
        t("map.layers.updateSavedSetError", "Could not update saved set"),
        (err as Error)?.message ?? ""
      );
    }
  };

  if (!open || !savedSet) return null;

  return (
    <div
      className="saved-set-dialog-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="saved-set-update-title"
    >
      <div className="saved-set-dialog">
        <h2 id="saved-set-update-title">
          {t("map.layers.updateSavedSetTitle", "Update saved set")}
        </h2>
        <form onSubmit={handleSubmit}>
          <div className="saved-set-dialog__field">
            <label htmlFor="saved-set-update-name">
              {t("map.layers.savedSetName", "Name")}
            </label>
            <input
              id="saved-set-update-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
              autoFocus
            />
          </div>
          <div className="saved-set-dialog__field">
            <label htmlFor="saved-set-update-municipality">
              {t("map.layers.municipality", "Municipality")}
            </label>
            <select
              id="saved-set-update-municipality"
              value={municipalityId}
              onChange={(e) => setMunicipalityId(e.target.value)}
            >
              <option value="">
                {t("map.layers.selectMunicipality", "Select municipality")}
              </option>
              {municipalityOptions.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name} ({m.code})
                </option>
              ))}
            </select>
          </div>
          <div className="saved-set-dialog__actions">
            <button type="button" className="map-layers-panel__btn" onClick={onClose}>
              {t("map.layers.cancel", "Cancel")}
            </button>
            <button
              type="submit"
              className="map-layers-panel__btn map-layers-panel__btn--primary"
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending
                ? t("map.layers.saving", "Saving...")
                : t("map.layers.save", "Save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
